import { get, post, put, del } from '../../apiClient';

/**
 * Field Sales Service
 * Handles all field sales related API calls
 */
const fieldSalesService = {
  /**
   * Get sales representatives for route plans
   * Fetches employees with roles: sales_manager, sales_executive, dispatch_manager, support_manager
   * Returns only: name, email, employeeId
   * 
   * @param {Object} params - Optional parameters (limit, search)
   * @returns {Promise<Object>} Response with salesReps array
   */
  getSalesReps: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.role) {
        // Support both array (['hr_manager','employee']) and string ('hr_manager,employee')
        const roleValue = Array.isArray(params.role)
          ? params.role.join(',')
          : params.role;
        queryParams.append('role', roleValue);
      }
      if (params.managerId) queryParams.append('managerId', params.managerId);

      const url = `/crm/field-sales/sales-reps${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await get(url);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data.salesReps || []
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch sales representatives'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch sales representatives'
      };
    }
  },

  /**
   * Get leads for route plan customer dropdown
   * Fetches active leads and returns: customer name, address, company name, phone, and email
   * 
   * @param {Object} params - Optional parameters (limit, search)
   * @returns {Promise<Object>} Response with leads array
   */
  getLeadsForDropdown: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);

      const url = `/crm/field-sales/leads-dropdown${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await get(url);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data.leads || []
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch leads'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch leads'
      };
    }
  },

  /**
   * Create a new route plan
   * 
   * @param {Object} routePlanData - Route plan data
   * @returns {Promise<Object>} Response with created route plan
   */
  createRoutePlan: async (routePlanData) => {
    try {
      const response = await post('/crm/field-sales/route-plans', routePlanData);

      if (response.responseCode === 201 && response.data) {
        return {
          success: true,
          data: response.data.routePlan
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create route plan'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to create route plan'
      };
    }
  },

  /**
   * Get all route plans with optional filters
   * 
   * @param {Object} filters - Optional filters (date, rep, search, status, limit)
   * @returns {Promise<Object>} Response with route plans array
   */
  getRoutePlans: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.date) queryParams.append('date', filters.date); // Backward compatibility
      if (filters.rep) queryParams.append('rep', filters.rep);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.page) queryParams.append('page', filters.page);

      const url = `/crm/field-sales/route-plans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await get(url);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data.routePlans || [],
          pagination: response.data.pagination
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch route plans'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch route plans'
      };
    }
  },

  /**
   * Get route plan by ID
   * 
   * @param {String} routePlanId - Route plan ID
   * @returns {Promise<Object>} Response with route plan
   */
  getRoutePlanById: async (routePlanId) => {
    try {
      const response = await get(`/crm/field-sales/route-plans/${routePlanId}`);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data.routePlan
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch route plan'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch route plan'
      };
    }
  },

  /**
   * Export a single route plan detail as a backend-generated Excel (.xlsx)
   * workbook and trigger the browser download.
   */
  exportRoutePlanDetail: async (routePlanId, fallbackName = 'RoutePlan') => {
    try {
      if (!routePlanId) {
        return { success: false, error: 'Route plan ID is required' };
      }

      const blob = await get(`/crm/field-sales/route-plans/${routePlanId}/export`, {
        responseType: 'blob'
      });

      const sanitized = String(fallbackName || 'RoutePlan')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'RoutePlan';
      const filename = `RoutePlan_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export route plan details'
      };
    }
  },

  /**
   * Export field sales dashboard statistics as a backend-generated Excel (.xlsx)
   * workbook and trigger browser download.
   * 
   * @param {Object} filters - Dashboard filter parameters (range, startDate, endDate)
   * @returns {Promise<Object>} Response object indicating success or failure
   */
  exportDashboardStats: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.range) queryParams.append('range', filters.range);

      const queryString = queryParams.toString();
      const url = `/crm/field-sales/dashboard-stats/export${queryString ? `?${queryString}` : ''}`;

      const blob = await get(url, { responseType: 'blob' });

      const filename = `Field_Sales_Dashboard.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export field sales dashboard'
      };
    }
  },

  /**
   * Update an existing route plan
   * 
   * @param {String} routePlanId - Route plan ID
   * @param {Object} routePlanData - Route plan data
   * @returns {Promise<Object>} Response with updated route plan
   */
  updateRoutePlan: async (routePlanId, routePlanData) => {
    try {
      const response = await put(`/crm/field-sales/route-plans/${routePlanId}`, routePlanData);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data.routePlan
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update route plan'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to update route plan'
      };
    }
  },

  /**
   * Get field sales dashboard statistics
   * 
   * @returns {Promise<Object>} Response with dashboard statistics
   */
  getFieldSalesStats: async () => {
    try {
      const response = await get('/crm/field-sales/stats');

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data.stats || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch field sales statistics'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch field sales statistics'
      };
    }
  },

  /**
   * Get dashboard statistics using aggregation
   * Returns KPIs, team performance, and recent visits
   * 
   * @returns {Promise<Object>} Response with aggregated dashboard statistics
   */
  getDashboardStats: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.range) queryParams.append('range', params.range);

      const url = `/crm/field-sales/dashboard-stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await get(url);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data.stats || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch dashboard statistics'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch dashboard statistics'
      };
    }
  },

  /**
   * Delete a route plan
   * 
   * @param {String} routePlanId - Route plan ID
   * @returns {Promise<Object>} Response with deletion result
   */
  deleteRoutePlan: async (routePlanId) => {
    try {
      const response = await del(`/crm/field-sales/route-plans/${routePlanId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Route plan deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete route plan'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to delete route plan'
      };
    }
  },

  /**
   * Bulk delete route plans
   * 
   * @param {Array<String>} ids - Array of route plan IDs
   * @returns {Promise<Object>} Response with deletion result
   */
  bulkDeleteRoutePlans: async (ids) => {
    try {
      const response = await post('/crm/field-sales/route-plans/bulk-delete', { ids });

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Route plans deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete route plans'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to delete route plans'
      };
    }
  },

  /**
   * Get customers for route plan dropdown
   * Fetches active customers and returns: customer name, address, company name, phone, and email
   * 
   * @param {Object} params - Optional parameters (limit, search)
   * @returns {Promise<Object>} Response with customers array
   */
  getCustomersForDropdown: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);

      const url = `/crm/field-sales/customers-dropdown${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await get(url);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data.customers || []
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch customers'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch customers'
      };
    }
  }
};

export default fieldSalesService;

